import type { HslaColor, HslColor } from 'react-colorful';
import tinycolor from 'tinycolor2';
import { SolidColor } from '../types';

export function solidColorToHslColor(
    color: string,
    opacity: number | undefined,
    latestHue: number | undefined,
): HslColor | HslaColor {
    const parsedColor = tinycolor(color);

    if (opacity !== undefined) {
        parsedColor.setAlpha(opacity / 100);
    }

    const hsl = parsedColor.toHsl();

    const out = {} as HslColor | HslaColor;

    // update color components to work with react-colorful
    Object.keys(hsl).forEach((key) => {
        if (key === 'a') {
            out[key] = hsl[key];
        } else if (key === 'h') {
            if (latestHue) {
                out[key] = hsl[key] || latestHue;
            } else {
                out[key] = hsl[key];
            }
        } else {
            out[key] = hsl[key] * 100;
        }
    });

    return out;
}

export function hslaToHex(h: number, s: number, l: number, a: number = 1): string {
    // Convert HSLA to RGBA first
    h /= 360;
    s /= 100;
    l /= 100;

    let r: number, g: number, b: number;

    if (s === 0) {
        r = g = b = l; // achromatic
    } else {
        const hue2rgb = (p: number, q: number, t: number): number => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };

        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;

        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    // Convert RGBA to HEX
    const toHex = (x: number): string => {
        const hex = Math.round(x * 255).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };

    const hex = `#${toHex(r)}${toHex(g)}${toHex(b)}`;

    // Handle alpha channel if it's not 1
    if (a < 1) {
        const alphaHex = Math.round(a * 255)
            .toString(16)
            .padStart(2, '0');
        return hex + alphaHex;
    }

    return hex.toUpperCase();
}

export function hslColorToSolidColor(color: HslColor | HslaColor): SolidColor {
    const { h, s, l } = color;
    const hex = hslaToHex(h, s, l);

    // If the color has an alpha channel, include it in the output
    if ('a' in color) {
        return {
            color: hex,
            opacity: color.a * 100,
        };
    }

    return hex;
}
